import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { newsList } from "@/mocks/news";
import { useSeo } from "@/hooks/useSeo";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = newsList.find((item) => item.id === id);

  useSeo({
    title: article ? `${article.title} - 飘花电影网` : "影视资讯 - 飘花电影网",
    description: article ? article.summary : "飘花电影网影视资讯详情",
    keywords: article ? `${article.category},影视资讯,${article.title}` : "影视资讯,飘花电影网",
  });

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col bg-background-100">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center justify-center px-4 py-20 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
            <i className="ri-article-line text-3xl" />
          </span>
          <h1 className="mt-4 font-heading text-xl font-bold text-foreground-900">未找到该资讯</h1>
          <p className="mt-2 text-sm text-foreground-500">资讯可能已被删除或链接有误</p>
          <Link to="/news" className="mt-6 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700">
            返回资讯列表
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = [...newsList]
    .filter((item) => item.id !== article.id && item.category === article.category)
    .slice(0, 4);

  return (
    <div className="flex min-h-screen flex-col bg-background-100">
      <SiteHeader />

      <main className="mx-auto w-full max-w-[900px] flex-1 px-4 py-6 md:px-6">
        <Breadcrumb items={[{ label: "资讯", path: "/news" }, { label: article.title }]} />

        <article className="mt-4 overflow-hidden rounded-xl border border-background-200 bg-background-50">
          <div className="h-56 w-full overflow-hidden bg-background-200 md:h-72">
            <img src={article.cover} alt={`${article.title}配图`} title={`${article.title} - 影视资讯`} className="h-full w-full object-top" />
          </div>
          <div className="p-5 md:p-8">
            <div className="flex flex-wrap items-center gap-2 text-xs text-foreground-400">
              <span className="rounded bg-secondary-100 px-2 py-0.5 text-secondary-700">{article.category}</span>
              <span>{article.date}</span>
              <span className="text-background-400">·</span>
              <span>{article.author}</span>
            </div>
            <h1 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-900 md:text-3xl">
              {article.title}
            </h1>
            <p className="mt-4 rounded-lg bg-background-100 px-4 py-3 text-sm leading-relaxed text-foreground-600">
              {article.summary}
            </p>
            <div className="mt-5 space-y-4 text-sm leading-relaxed text-foreground-700 md:text-[15px]">
              {article.content.map((paragraph, i) => (
                <p key={i}>{paragraph}</p>
              ))}
            </div>
            <p className="mt-6 flex items-center gap-2 rounded-md bg-secondary-50 px-3 py-2 text-xs text-secondary-800">
              <i className="ri-information-line" />
              本文为演示内容，仅用于展示资讯模块样式。
            </p>
          </div>
        </article>

        {related.length > 0 && (
          <section className="mt-8">
            <h2 className="mb-4 flex items-center gap-2 border-b-2 border-background-200 pb-2.5 font-heading text-lg font-bold text-foreground-900">
              <span className="h-5 w-1 rounded-full bg-primary-600" />
              相关阅读
            </h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {related.map((item) => (
                <Link
                  key={item.id}
                  to={`/news/${item.id}`}
                  className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-all hover:border-primary-300"
                >
                  <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img src={item.cover} alt={`${item.title}配图`} className="h-full w-full object-top" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="line-clamp-2 text-sm font-medium text-foreground-800 transition-colors group-hover:text-primary-700">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-400">{item.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}